/**
 * Performance utilities for optimizing app loading
 */

export const preloadRoute = (routeImportFunction: () => Promise<unknown>) => {
  // Preload a route component
  return routeImportFunction();
};

export const prefetchAssets = (urls: string[]) => {
  // Prefetch critical assets
  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  });
};

export const preloadCriticalResources = () => {
  // Preload critical resources
  const criticalResources = [
    '/api/v1/auth/me', // User info endpoint
    '/api/v1/dashboard', // Dashboard data endpoint
  ];

  criticalResources.forEach(url => {
    // Use requestIdleCallback to avoid blocking main thread
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        fetch(url, {
          method: 'HEAD',
          credentials: 'include',
        }).catch(() => {
          // Silently fail - this is just prefetching
        });
      });
    }
  });
};

export const addServiceWorker = () => {
  // Register service worker for caching
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then(() => {
          // Service worker registered successfully
        })
        .catch(() => {
          // Service worker registration failed - continue without caching
        });
    });
  }
};

export const optimizeImages = () => {
  // Add loading="lazy" to images not in viewport
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    if (!img.hasAttribute('loading')) {
      img.setAttribute('loading', 'lazy');
    }
  });
};

export const measurePerformance = () => {
  // Measure performance metrics (logging disabled for production)
  if ('performance' in window && process.env.NODE_ENV === 'development') {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

        // Performance metrics available in development only
        const metrics = {
          'DNS Lookup': perfData.domainLookupEnd - perfData.domainLookupStart,
          'TCP Connection': perfData.connectEnd - perfData.connectStart,
          Request: perfData.responseStart - perfData.requestStart,
          Response: perfData.responseEnd - perfData.responseStart,
          'DOM Processing': perfData.domComplete - perfData.domInteractive,
          'Total Load Time': perfData.loadEventEnd - perfData.fetchStart,
        };

        // Store in window for debugging if needed
        (window as typeof window & { __performanceMetrics?: object }).__performanceMetrics = metrics;
      }, 0);
    });
  }
};
